import { PhoneNumberFormat } from 'google-libphonenumber';
import { isEmpty } from 'lodash';
import { observable, makeObservable, action, computed } from 'mobx';
import { IPersonModel } from 'Models/PersonModel';
import phoneUtil, { isPhoneNumberMatch } from 'Utils/phoneUtil';
import { SourceType } from './SourceModelV2';

// String types

export type TPhoneNumber =
  | 'business'
  | 'home'
  | 'mobile'
  | 'pager'
  | 'business_fax'
  | 'home_fax'
  | 'organization_main'
  | 'assistant'
  | 'radio'
  | 'other';

export type TLineType = 'line' | 'direct';

export type TEmail = 'work' | 'personal' | 'other';

export type TSourcePlatform = 'Bhive' | 'ContactAPI' | 'Nylas';

// Interfaces

export interface IPhoneNumber {
  id?: string;
  number: string;
  normalizedNumber?: string;
  type: TPhoneNumber;
  descriptor?: string;
  primary?: boolean;
  smsEnabled?: boolean;
  lineType?: TLineType;
}

export interface IEmail {
  address: string;
  type: TEmail;
  descriptor: string;
}

export interface ISourceData {
  type: SourceType;
  contactId: string;
  accountId: string;
  package: string;
}

export interface IExtensionNumber {
  number: string;
  descriptor?: string;
}

// DTOs

export interface IContact {
  id: string;
  sourceId?: string;
  tenantId?: number;
  customerId: number;
  phoneNumbers?: IPhoneNumber[];
  extensionNumbers: IExtensionNumber[];
  emails?: IEmail[];
  firstName: string;
  middleName?: string;
  lastName: string;
  name?: string;
  organization?: string;
  role?: string;
  userId?: string;
  department?: string;
  avatarUrl?: string;
  sourceData: ISourceData;
  source?: TSourcePlatform;
  createdAt: number;
  updatedAt: number;
  timezone?: string;
  notes?: string;
  hasAvatar: boolean;
  voicemailInboxId?: string;
  deleted?: boolean;
}

export interface IPusherContact
  extends Omit<IContact, 'id' | 'createdAt' | 'updatedAt'> {
  id: number;
  uuid: string;
  providerContactId?: string;
  providerAccountId?: string;
  organizationRole?: string;
  platformUserId?: number;
  platformAccountId?: number;
  hasProfilePicture: boolean;
  pictureKey?: string;
  accountId?: string;
  pictureUrl?: string;
  canReceiveSms?: boolean;
  sourceV2?: TSourcePlatform;
  createdAt: string;
  updatedAt: string;
}

export interface IAvatarUploadResponse {
  url: string;
  key: string;
  fields: any;
}

// Helper functions
const convertCommonProperties = (
  dto: IContact | IPusherContact
): ContactModelV2 => {
  const contactModel = new ContactModelV2();

  contactModel.sourceId = dto.sourceId;
  contactModel.tenantId = dto.tenantId;
  contactModel.customerId = dto.customerId;
  contactModel.phoneNumbers = dto.phoneNumbers;
  contactModel.extensionNumbers = dto.extensionNumbers;
  contactModel.emails = dto.emails;
  contactModel.firstName = dto.firstName;
  contactModel.middleName = dto.middleName;
  contactModel.lastName = dto.lastName;
  contactModel.name = dto.name;
  contactModel.organization = dto.organization;
  contactModel.role = dto.role;
  contactModel.userId = dto.userId;
  contactModel.department = dto.department;
  contactModel.avatarUrl = dto.avatarUrl;
  contactModel.sourceData = dto.sourceData;
  contactModel.source = dto.source;
  contactModel.timezone = dto.timezone;
  contactModel.notes = dto.notes;
  contactModel.hasAvatar = dto.hasAvatar;
  contactModel.voicemailInboxId = dto.voicemailInboxId;
  contactModel.deleted = dto.deleted;

  return contactModel;
};

// Contact Model

export class ContactModelV2 {
  @observable id: string;
  @observable sourceId?: string;
  @observable tenantId?: number;
  @observable customerId: number;
  @observable phoneNumbers?: IPhoneNumber[];
  @observable extensionNumbers: IExtensionNumber[];
  @observable emails?: IEmail[];
  @observable firstName: string;
  @observable middleName?: string;
  @observable lastName: string;
  @observable name?: string;
  @observable organization?: string;
  @observable role?: string;
  @observable userId?: string;
  @observable department?: string;
  @observable avatarUrl?: string;
  @observable sourceData: ISourceData;
  @observable source?: TSourcePlatform;
  @observable createdAt: number;
  @observable updatedAt: number;
  @observable timezone?: string;
  @observable notes?: string;
  @observable hasAvatar: boolean;
  @observable voicemailInboxId?: string;
  @observable deleted?: boolean;

  constructor(contact?: ContactModelV2) {
    makeObservable(this);

    if (contact) {
      this.id = contact.id;
      this.sourceId = contact.sourceId;
      this.tenantId = contact.tenantId;
      this.customerId = contact.customerId;
      this.phoneNumbers = contact.phoneNumbers;
      this.extensionNumbers = contact.extensionNumbers;
      this.emails = contact.emails;
      this.firstName = contact.firstName;
      this.middleName = contact.middleName;
      this.lastName = contact.lastName;
      this.name = contact.name;
      this.organization = contact.organization;
      this.role = contact.role;
      this.userId = contact.userId;
      this.department = contact.department;
      this.avatarUrl = contact.avatarUrl;
      this.sourceData = contact.sourceData;
      this.source = contact.source;
      this.createdAt = contact.createdAt;
      this.updatedAt = contact.updatedAt;
      this.timezone = contact.timezone;
      this.notes = contact.notes;
      this.hasAvatar = contact.hasAvatar;
      this.voicemailInboxId = contact.voicemailInboxId;
      this.deleted = contact.deleted;
    }
  }

  @action
  static fromResponseDto(dto: IContact): ContactModelV2 {
    const contactModel = convertCommonProperties(dto);

    contactModel.id = dto.id;
    contactModel.createdAt = dto.createdAt;
    contactModel.updatedAt = dto.updatedAt;

    return contactModel;
  }

  @action
  static fromPusherContact(dto: IPusherContact): ContactModelV2 {
    const contactModel = convertCommonProperties(dto);

    contactModel.id = dto.uuid;
    contactModel.source = dto.sourceV2;
    contactModel.createdAt = Number.parseInt(dto.createdAt);
    contactModel.updatedAt = Number.parseInt(dto.updatedAt);

    return contactModel;
  }

  /**
   * This static function creates a ContactModelV2 instance from a PersonModel instance.
   * @warning This function should only be used for the logged in user.
   */
  @action
  static fromLoggedPerson(person: IPersonModel): ContactModelV2 {
    const contactModel = new ContactModelV2();

    contactModel.id = '';
    contactModel.sourceId = (person.id as number).toString();
    contactModel.tenantId = person.locationId;
    contactModel.customerId = undefined;
    contactModel.phoneNumbers = [
      { number: person.phoneNumber, type: 'other', primary: true }, // check type
    ];

    if (person.lines && person.lines.length) {
      contactModel.phoneNumbers = person.lines.map((line) => ({
        number: line.callerId,
        type: 'business',
        primary: line.id === person.primaryLineId,
        smsEnabled: line.allowSms,
        lineType: 'line',
      }));
    }

    if (person.extensionNumber) {
      contactModel.extensionNumbers = [{ number: person.extensionNumber }];
    }

    contactModel.emails = person.email
      ? [{ address: person.email, type: 'other', descriptor: '' }]
      : undefined;

    contactModel.firstName = person.firstName;
    contactModel.lastName = person.lastName;
    contactModel.name = `${person.firstName} ${person.lastName}`;
    contactModel.organization = undefined;
    contactModel.role = person.role;
    contactModel.userId = undefined;
    contactModel.department = person.department;
    contactModel.avatarUrl = person.profilePictureUrl;
    contactModel.sourceData = { package: person.package } as ISourceData;
    contactModel.source = 'Bhive';
    contactModel.createdAt = Number.parseInt(person.created);
    contactModel.updatedAt = Number.parseInt(person.created);
    contactModel.timezone = undefined;
    contactModel.notes = undefined;
    contactModel.hasAvatar = !!person.profilePictureUrl;
    contactModel.voicemailInboxId = person.voicemailInboxId?.toString();

    return contactModel;
  }

  @computed
  get DisplayAvatar() {
    let displayAvatar = this.avatarUrl;
    if (!isEmpty(displayAvatar)) {
      if (displayAvatar !== undefined && displayAvatar.indexOf('?') > 0) {
        displayAvatar = displayAvatar.substring(0, displayAvatar.indexOf('?'));
      }

      displayAvatar += '?d=identicon&s=90';
    } else {
      displayAvatar = 'https://www.gravatar.com/avatar?d=identicon&s=90';
    }
    return displayAvatar;
  }

  @computed
  get primaryPhoneNumber(): IPhoneNumber | undefined {
    if (!this.phoneNumbers?.length) {
      return undefined;
    }

    return (
      this.phoneNumbers?.find((phoneNumber) => phoneNumber.primary) ||
      this.phoneNumbers[0]
    );
  }

  @computed
  get phoneNumber(): string | undefined {
    return this.primaryPhoneNumber?.number;
  }

  @computed
  get primaryLineId(): string | undefined {
    return this.primaryPhoneNumber?.id;
  }

  @computed
  get mobileNumber(): string | undefined {
    return this.phoneNumbers?.find(
      (phoneNumber) => phoneNumber.type === 'mobile'
    )?.number;
  }

  @computed
  get lines(): IPhoneNumber[] | undefined {
    return this.phoneNumbers?.filter(
      (phoneNumber) => phoneNumber.lineType === 'line' && phoneNumber.number
    );
  }

  @computed
  get nonDuplicatePhoneNumbers(): IPhoneNumber[] | undefined {
    if (!this.phoneNumbers) {
      return undefined;
    }

    const seen: { number: string; type: string }[] = [];
    return this.phoneNumbers.filter((phone) => {
      if (
        seen.some(
          (seenPhone) =>
            seenPhone.number === phone.number && seenPhone.type === phone.type
        )
      ) {
        return false;
      }
      seen.push({ number: phone.number, type: phone.type });
      return true;
    });
  }

  @computed
  get nonDuplicateEmails(): IEmail[] | undefined {
    if (!this.emails) {
      return undefined;
    }

    const seen: { address: string; type: string }[] = [];
    return this.emails.filter((email) => {
      if (
        seen.some(
          (seenEmail) =>
            seenEmail.address === email.address && seenEmail.type === email.type
        )
      ) {
        return false;
      }
      seen.push({ address: email.address, type: email.type });
      return true;
    });
  }

  @computed
  get email(): string | undefined {
    return this.emails && this.emails.length
      ? this.emails[0]?.address
      : undefined;
  }

  @computed
  get extensionNumber(): string | undefined {
    return this.extensionNumbers && this.extensionNumbers.length
      ? this.extensionNumbers[0].number
      : undefined;
  }

  @observable
  public formatNumber = (): string => {
    let phoneNumber: string;
    for (let i = 0; i < this.lines.length; i++) {
      if (this.lines[i].number) {
        phoneNumber = this.lines[i].number;
      } else {
        phoneNumber = this.primaryLineId.toString();
      }
    }

    if (phoneNumber) {
      if (phoneUtil.isPossibleNumberString(phoneNumber, 'US')) {
        const parsePhoneNumber = phoneUtil.parse(phoneNumber, 'US');
        return phoneUtil.format(parsePhoneNumber, PhoneNumberFormat.NATIONAL);
      } else {
        return null;
      }
    } else {
      return null;
    }
  };

  @observable
  public getMatchingPhoneNumber = (
    phoneNumber: string
  ): IPhoneNumber | undefined => {
    return this.phoneNumbers?.find((phone) =>
      isPhoneNumberMatch(phoneNumber, phone.number)
    );
  };

  static formatNumber(phoneNumber: string): string {
    if (phoneUtil.isPossibleNumberString(phoneNumber, 'US')) {
      const parsePhoneNumber = phoneUtil.parse(phoneNumber, 'US');
      return phoneUtil.format(parsePhoneNumber, PhoneNumberFormat.NATIONAL);
    } else {
      return phoneNumber;
    }
  }
}

// Internal interfaces

export interface IFormContact
  extends Partial<
    Omit<IContact, 'sourceData' | 'phoneNumbers' | 'emails' | 'hasAvatar'>
  > {
  sourceData?: Partial<ISourceData>;
  phoneNumbers?: IFormPhoneNumber[];
  emails?: IFormEmail[];
}

export interface IFormPhoneNumber extends Partial<IPhoneNumber> {
  guid?: number;
}

export interface IFormEmail extends Partial<IEmail> {
  guid?: number;
}
